import React, { FC, useEffect, useMemo, useState } from 'react';
import { endOfDay, format, isToday } from 'date-fns';

import { setLessonType, setReservationDate } from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import useEventTracking, {
  EventType,
} from 'infrastructure/targets/web/modules/common/hooks/useEventTracking';
import DateAndLessonTypeSelector from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/DateAndLessonTypeSelector';
import InstructorsList from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/InstructorsList';
import FirstTimeLessonCardCompact from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/FirstTimeLessonCardCompact';

import { StyledCardsContainer, StyledContentContainer } from './style';
import { StaffAppointmentAvailability } from 'common/infrastructure/modules/appointment/interfaces/AvailabilityTypes';
import {
  selectLocation,
  selectReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.selector';

interface ILessonByDate {
  instructorsList: StaffAppointmentAvailability[] | undefined;
  getInstructors: any;
  email: string | null;
  instructorsLoading: boolean;
  nextStep: (selectedInstructorId: number | null, isFirstTimeLesson?: boolean) => void;
}

const LessonByDate: FC<ILessonByDate> = ({
  instructorsList,
  getInstructors,
  email,
  instructorsLoading,
  nextStep,
}) => {
  const dispatch = useAppDispatch();
  const { sendEvent } = useEventTracking();
  const selectedLocationId = useAppSelector(selectLocation);
  const reservationDate = useAppSelector(selectReservationDate);

  const [instructorsToShow, setInstructorsToShow] = useState(instructorsList);
  const [firstTimeLessonService, setFirstTimeLessonService] = useState<any>(null);
  const [selectedDateNotToday, setSelectedDateNotToday] = useState<boolean>(false);

  // on page load, set reservation date to tomorrow
  useEffect(() => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    dispatch(setReservationDate({ reservationDate: `${tomorrow}` }));
    setSelectedDateNotToday(!isToday(tomorrow));
  }, [dispatch]);

  // Re-fetch instructors when location changes, using the currently selected date
  useEffect(() => {
    if (selectedLocationId && reservationDate) {
      const currentDate = new Date(reservationDate);
      getInstructors({
        locationId: selectedLocationId,
        staffIds: [],
        startDateTime: format(currentDate, 'yyyy-MM-dd'),
        endDateTime: format(endOfDay(currentDate), 'yyyy-MM-dd'),
        email,
        includeLastBooked: true,
      });
    }
  }, [selectedLocationId, reservationDate, email, getInstructors]);

  const handleDateChange = (newDate: Date | undefined) => {
    if (newDate) {
      dispatch(setReservationDate({ reservationDate: `${newDate}` }));
      setSelectedDateNotToday(!isToday(newDate));
      // Instructor refetch will be handled automatically by the useEffect watching reservationDate
    }
  };

  const handleLessonTypeChange = (lessonType: string | undefined) => {
    if (lessonType) {
      const filteredInstructors = instructorsList?.filter((instructor) =>
        instructor.services.some((service) => service.sessionTypeId === Number(lessonType)),
      );
      setInstructorsToShow(filteredInstructors);
    }
  };

  const getFirstTimeLessonContent = (): string =>
    `The Swing Evaluation is a 60-minute session designed to discuss your golf history and any frustrations you are currently having with your game. ` +
    `Then, together, we'll work to establish measurable goals. We will introduce you to the TrackMan technology and evaluate your swing based on ` +
    `seeing you hit multiple different shots, before identifying the root cause of your inconsistencies. ` +
    `Most importantly, we will develop a custom improvement plan to get you on the path towards achieving your golfing dreams!`;

  const convertLessonTypes = useMemo(() => {
    if (!instructorsList || instructorsList.length === 0) {
      return [];
    }

    const uniqueLessonTypes = new Map();
    instructorsList.forEach((instructor) => {
      instructor.services.forEach((service) => {
        const sessionTypeId =
          typeof service.sessionTypeId === 'number'
            ? service.sessionTypeId
            : parseInt(service.sessionTypeId.toString());

        if (!uniqueLessonTypes.has(sessionTypeId)) {
          uniqueLessonTypes.set(sessionTypeId, {
            title: service.title,
            label: '',
            additionalValue:
              typeof service.defaultTimeLength === 'number'
                ? service.defaultTimeLength
                : parseFloat(service.defaultTimeLength?.toString() || '0'),
            value: sessionTypeId,
            subtitle: service.price?.toString() || null,
          });
        }
      });
    });

    return Array.from(uniqueLessonTypes.values());
  }, [instructorsList]);

  const handleCardClick = (value: number) => {
    nextStep(value);
    sendEvent(EventType.SelectExperience, {
      experience: 'lesson',
      instructor: instructorsList?.find((i) => i.staffId === value),
      utm: localStorage.getItem('utm') ? JSON.parse(localStorage.getItem('utm') as string) : null,
    });
  };

  const handleSelectFirstTimeLesson = () => {
    if (firstTimeLessonService) {
      dispatch(setLessonType({ lessonType: firstTimeLessonService.sessionTypeId }));
    }
    nextStep(null, true);
    sendEvent(EventType.SelectExperience, {
      experience: 'lesson',
      instructor: 'Swing Evaluation',
      utm: localStorage.getItem('utm') ? JSON.parse(localStorage.getItem('utm') as string) : null,
    });
  };

  const filterNonLessonInstructors = () =>
    // filter out instructors who don't teach at least one lesson type
    instructorsList?.filter(
      (instructor) =>
        instructor.services.some((service) =>
          service.title.toLocaleLowerCase().includes('lesson'),
        ) ||
        instructor.services.some((service) => service.title.toLocaleLowerCase().includes('swing')),
    );

  useEffect(() => {
    setInstructorsToShow(filterNonLessonInstructors());
    if (instructorsList?.length) {
      const firstTimeLessonType = instructorsList
        .flatMap((instructor) => instructor.services)
        .find((service) => service.title.toLocaleLowerCase().includes('swing eval'));
      setFirstTimeLessonService(firstTimeLessonType ?? null);
    } else {
      setFirstTimeLessonService(null);
    }
  }, [instructorsList]);

  return (
    <StyledContentContainer>
      <DateAndLessonTypeSelector
        onDateChange={handleDateChange}
        onLessonTypeChange={handleLessonTypeChange}
        lessonTypes={convertLessonTypes}
      />
      {firstTimeLessonService && selectedDateNotToday && (
        <FirstTimeLessonCardCompact
          instructorsLoading={instructorsLoading}
          onCardClick={handleSelectFirstTimeLesson}
          content={getFirstTimeLessonContent()}
        />
      )}
      <StyledCardsContainer>
        <InstructorsList
          loading={instructorsLoading}
          onCardClick={handleCardClick}
          instructorsList={instructorsToShow}
        />
      </StyledCardsContainer>
    </StyledContentContainer>
  );
};

export default LessonByDate;
